import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Languages } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Language switcher.
 *
 * Task #32 fix:
 *   - Uses Radix's canonical `onSelect` handler (NOT `onClick`). On a
 *     Radix `DropdownMenuItem`, `onClick` is composed with Radix's
 *     internal close-handler — the menu close races with the user's
 *     handler in some pointer/keyboard flows, and on keyboard
 *     selection (Enter/Space) `onClick` doesn't fire at all. The
 *     symptom was: clicking a language did nothing in-session,
 *     localStorage.i18nextLng was never updated.
 *   - The `dir`/`lang` document attributes are NOT set here anymore.
 *     They're applied globally in `src/lib/i18n.ts` (a single
 *     `languageChanged` listener + bootstrap-time application) so
 *     RTL works on every route — including routes that don't render
 *     this switcher, like /login.
 */
export const LanguageSwitcher: React.FC = () => {
    const { i18n, t } = useTranslation();

    // Triggers Radix's onSelect; we ignore the event payload.
    const changeLanguage = (lng: string) => {
        // i18n.changeLanguage returns a Promise. We don't await — any
        // namespace load is cached after first run, and unhandled
        // rejections are logged by i18next's debug mode.
        void i18n.changeLanguage(lng);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 border-gray-300">
                    <Languages className="h-4 w-4" />
                    <span>{t('common.language')}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => changeLanguage('en')} className="cursor-pointer">
                    English
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => changeLanguage('ar')} className="cursor-pointer">
                    العربية
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
